<template>
  <div
    v-if="availsResult && dataLoaded"
    class="avails-result-wrapper">
    <div class="title">{{ title }}</div>
    <div
      v-if="isPurchaseTypeCadence"
      class="cadence-details">
      <div>{{ capitalize(i18n.global.t('names.selectedCadence')) }} {{ targetedCadence }}</div>
      <span>|</span>
      <div>
        {{ capitalize(i18n.global.t('names.maximumCadenceAvailable')) }}
        {{ availsResult.totalImpressionsResult?.maxCadenceAvailable }}
      </div>
      <span>|</span>
      <div>
        {{ capitalize(i18n.global.t('names.maximumHistoricalCadence')) }}
        {{ availsResult.totalImpressionsResult?.maxHistoricalCadence }}
      </div>
    </div>

    <div class="chart">
      <VueEcharts
        ref="echart"
        :option="chartOptions"
        :autoresize="true" />
    </div>

    <div class="caption-columns">
      <div class="caption-column">
        <ColorCaption :color="DELIVERY_GOAL_COLORS[DeliveryGoals.Delivered]" />
        <div class="caption-value">{{ formatNumber(deliveredImpressions, '0,0') }}</div>
        <div class="caption-label">{{ $t('availsResult.categories.labels.delivered') }}</div>
        <ColorCaption
          :color="DELIVERY_GOAL_COLORS[DeliveryGoals.LikelyToDeliver]"
          :striped="true" />
        <div class="caption-value">{{ formatNumber(likelyToDeliver, '0,0') }}</div>
        <div class="caption-label">{{ $t('availsResult.categories.labels.likelyToDeliver') }}</div>
        <ColorCaption
          :color="DELIVERY_GOAL_COLORS[DeliveryGoals.Competing]"
          :striped="true" />
        <div class="caption-value">{{ formatNumber(competingGoal, '0,0') }}</div>
        <div class="caption-label">{{ $t('availsResult.categories.labels.competing') }}</div>
        <ColorCaption :color="DELIVERY_GOAL_COLORS[DeliveryGoals.Unavailable]" />
        <div class="caption-value">{{ formatNumber(unavailable, '0,0') }}</div>
        <div class="caption-label">{{ $t('availsResult.categories.labels.unavailable') }}</div>
      </div>
      <div class="caption-column">
        <template v-if="useDetailedForecast">
          <ColorCaption :color="getCategoryColor(InventoryForecasts.Available, 'reserved')" />
          <div class="caption-value">{{ formatNumber(availableReserved, '0,0') }}</div>
          <div class="caption-label">{{ $t('availsResult.categories.labels.availableReserved') }}</div>
          <ColorCaption :color="getCategoryColor(InventoryForecasts.Available, 'unreserved')" />
          <div class="caption-value">{{ formatNumber(availableUnreserved, '0,0') }}</div>
          <div class="caption-label">{{ $t('availsResult.categories.labels.availableUnreserved') }}</div>
        </template>
        <template v-else>
          <ColorCaption :color="INVENTORY_FORECAST_COLORS[InventoryForecasts.Available]" />
          <div class="caption-value">{{ formatNumber(available, '0,0') }}</div>
          <div class="caption-label">{{ $t('availsResult.categories.labels.available') }}</div>
        </template>
        <ColorCaption :color="INVENTORY_FORECAST_COLORS[InventoryForecasts.Competing]" />
        <div class="caption-value">{{ formatNumber(competing, '0,0') }}</div>
        <div class="caption-label">
          <span>{{ $t('availsResult.categories.labels.competing') }}</span>
          <i>{{ $t('availsResult.description.competing') }}</i>
        </div>
        <ColorCaption :color="INVENTORY_FORECAST_COLORS[InventoryForecasts.Booked]" />
        <div class="caption-value">{{ formatNumber(booked, '0,0') }}</div>
        <div class="caption-label">
          <span>{{ $t('availsResult.categories.labels.booked') }}</span>
          <i>{{ $t('availsResult.description.unavailable') }}</i>
        </div>
        <ColorCaption
          :color="INVENTORY_FORECAST_COLORS[InventoryForecasts.TotalCapacity]"
          :show-border="true" />
        <div class="caption-value">{{ formatNumber(totalImpressionCapacity, '0,0') }}</div>
        <div class="caption-label">{{ $t('availsResult.categories.labels.totalCapacity') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import * as echarts from 'echarts';
  import { DateTime } from 'luxon';
  import { capitalize, computed, ComputedRef, onMounted, Ref, ref, toRefs } from 'vue';
  import VueEcharts from 'vue-echarts';

  import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
  import { AvailsResult, LineItem, Package, Plan } from '@/common/models';
  import { AVAILS_PURCHASE_TYPE_PRIORITY } from '@/common/models/avails/TotalImpressionsByUnit';
  import PURCHASE_TYPES_BY_PRIORITY from '@/common/models/PurchaseTypePriority';
  import formatNumber from '@/utils/NumberFormatter';
  import i18n from '@/vueI18n';

  import { createEntityAvailsDataHelper } from './avails-result-helpers/EntityAvailsDataHelperFactory';
  import AvailsResultEnum from './AvailsResult.enum';
  import AvailsResultExportHelper from './AvailsResultExportHelper';
  import ColorCaption from './ColorCaption.vue';
  import ForecastMode from './ForcastMode.enum';

  const TITLES = {
    [AvailsResultEnum.Available]: 'availsResult.title.likelyToDeliver',
    [AvailsResultEnum.MayBeAvailable]: 'availsResult.title.atRiskOfUnderDelivering',
    [AvailsResultEnum.PartiallyAvailable]: 'availsResult.title.atRiskOfUnderDelivering',
    [AvailsResultEnum.NotAvailable]: 'availsResult.title.unavailable',
  };

  enum DeliveryGoals {
    Delivered,
    LikelyToDeliver,
    Competing,
    Unavailable,
  }

  enum InventoryForecasts {
    Available,
    Competing,
    Booked,
    TotalCapacity,
  }

  const DELIVERY_GOAL_COLORS = {
    [DeliveryGoals.Delivered]: '#008221',
    [DeliveryGoals.LikelyToDeliver]: '#00e339',
    [DeliveryGoals.Competing]: '#fab300',
    [DeliveryGoals.Unavailable]: '#fa2e00',
  };

  const INVENTORY_FORECAST_COLORS = {
    [InventoryForecasts.Available]: '#7704c4',
    [InventoryForecasts.Competing]: '#0464c4',
    [InventoryForecasts.Booked]: '#c43e04',
    [InventoryForecasts.TotalCapacity]: '#ffffff',
  };

  function getCategoryColor(inventoryForecast: InventoryForecasts, status: 'reserved' | 'unreserved') {
    const suffix = status === 'unreserved' ? '80' : '';
    return `${INVENTORY_FORECAST_COLORS[inventoryForecast]}${suffix}`;
  }

  interface IProps {
    entity: LineItem | Plan | Package;
    useDetailedForecast: boolean;
    forecastMode: ForecastMode;
    startDate: DateTime;
    endDate: DateTime;
    availsResult?: AvailsResult;
    purchaseType: PurchaseTypesEnum;
  }

  const props = defineProps<IProps>();

  const { entity, useDetailedForecast, forecastMode, availsResult, purchaseType } = toRefs(props);

  const echart: Ref<echarts.ECharts> = ref();

  const title = computed(() => {
    if (availsResult.value == null) {
      return null;
    }

    return i18n.global.t(TITLES[availsResult.value.availabilityResult], [formatNumber(deliveryGoalTotal.value, '0,0')]);
  });

  const deliveryGoalTotal = ref(0);

  const estimatedTotalImpressionCapacity = ref(0);

  const deliveredImpressions = ref(0);

  const dataLoaded = ref(false);

  const targetedCadence = ref<number>(null);

  // Forecast

  /**
   * Sum of available reserved impressions
   */
  const availableReserved = computed(() => {
    const reservedImpressionsByUnit = availsResult.value?.totalImpressionsByUnit.reservedImpressionsByUnit;

    if (reservedImpressionsByUnit == null) {
      return 0;
    }

    const result = Object.values(reservedImpressionsByUnit).reduce((total, impressions) => {
      total += impressions;
      return total;
    }, 0);

    return Math.round(result);
  });

  /**
   * Sum of available unreserved impressions
   */
  const availableUnreserved = computed(() => {
    const unreservedImpressionsByUnit = availsResult.value?.totalImpressionsByUnit.unreservedImpressionsByUnit;

    if (unreservedImpressionsByUnit == null) {
      return 0;
    }

    const result = Object.values(unreservedImpressionsByUnit).reduce((total, impressions) => {
      total += impressions;
      return total;
    }, 0);

    return Math.round(result);
  });

  /**
   * Sum of available reserved and unreserved impressions
   */
  const available = computed(() => availableReserved.value + availableUnreserved.value);

  /**
   * Sum of competing impressions from other entities with same priority
   */
  const competing = computed(() => {
    if (availsResult.value == null) {
      return 0;
    }
    const { bookedImpressionsByPurchaseTypeByUnit } = availsResult.value.totalImpressionsByUnit;
    if (bookedImpressionsByPurchaseTypeByUnit == null) {
      return 0;
    }

    const currentEntityPriority = PURCHASE_TYPES_BY_PRIORITY.indexOf(purchaseType.value);

    const result = Object.entries(bookedImpressionsByPurchaseTypeByUnit)
      // Find other entities with the same priority
      .filter(([availsPurchaseType]) => {
        const otherEntityPriority = AVAILS_PURCHASE_TYPE_PRIORITY[availsPurchaseType];
        return otherEntityPriority === currentEntityPriority;
      })
      // Sum booked impressions
      .map(([, bookedImpressions]) => Object.values(bookedImpressions))
      .reduce((cummulatedImpressions, impressions) => {
        cummulatedImpressions.push(...impressions);
        return cummulatedImpressions;
      }, [])
      .reduce((total, impressions) => {
        total += impressions;
        return total;
      }, 0);

    return Math.round(result);
  });

  /**
   * Sum of impressions booked by other entities with higher priority
   */
  const booked = computed(() => {
    if (availsResult.value == null) {
      return 0;
    }
    const { bookedImpressionsByPurchaseTypeByUnit } = availsResult.value.totalImpressionsByUnit;
    if (bookedImpressionsByPurchaseTypeByUnit == null) {
      return 0;
    }

    const currentEntityPriority = PURCHASE_TYPES_BY_PRIORITY.indexOf(purchaseType.value);

    const result = Object.entries(bookedImpressionsByPurchaseTypeByUnit)
      // Find other entities with higher priority
      .filter(([availsPurchaseType]) => {
        const otherEntityPriority = AVAILS_PURCHASE_TYPE_PRIORITY[availsPurchaseType];
        return otherEntityPriority < currentEntityPriority;
      })
      // Sum booked impressions
      .map(([, bookedImpressions]) => Object.values(bookedImpressions))
      .reduce((cummulatedImpressions, impressions) => {
        cummulatedImpressions.push(...impressions);
        return cummulatedImpressions;
      }, [])
      .reduce((total, impressions) => {
        total += impressions;
        return total;
      }, 0);

    return Math.round(result);
  });

  const totalImpressionCapacity = computed(() => {
    // If availsResult is null, fallback to estimated total impression capacity
    if (availsResult.value == null) {
      return estimatedTotalImpressionCapacity.value;
    }
    const { impressionCapacityByUnit } = availsResult.value.totalImpressionsByUnit;
    // Sum of units total capacities
    const sumOfUnitsCapacity = Object.values(impressionCapacityByUnit).reduce((sum, capacity) => {
      sum += capacity;
      return sum;
    }, 0);

    return Math.round(sumOfUnitsCapacity);
  });

  const remainder = computed(() =>
    Math.max(
      0,
      totalImpressionCapacity.value - deliveredImpressions.value - available.value - competing.value - booked.value,
    ),
  );

  // Delivery Goals
  const likelyToDeliver = computed(() => {
    if (deliveryGoalTotal.value < deliveredImpressions.value) {
      return 0;
    }
    const competingThreshold = deliveredImpressions.value + available.value;
    if (deliveryGoalTotal.value > competingThreshold) {
      return available.value;
    }

    return deliveryGoalTotal.value - deliveredImpressions.value;
  });

  const competingGoal = computed(() => {
    const competingThreshold = deliveredImpressions.value + available.value;
    if (deliveryGoalTotal.value < competingThreshold) {
      return 0;
    }
    const bookedThreshold = competingThreshold + competing.value;
    if (deliveryGoalTotal.value > bookedThreshold) {
      return competing.value;
    }

    const result = deliveryGoalTotal.value - competingThreshold;
    return Math.round(result);
  });

  const unavailable = computed(() => {
    const bookedThreshold = deliveredImpressions.value + available.value + competing.value;

    if (deliveryGoalTotal.value < bookedThreshold) {
      return 0;
    }

    const result = deliveryGoalTotal.value - bookedThreshold;
    return Math.round(result);
  });

  const isPurchaseTypeCadence = computed(() => entity.value.purchaseType === PurchaseTypesEnum.CADENCE);

  function getTargetedCadence() {
    if (entity.value instanceof LineItem) {
      return entity.value.cadence;
    }
    if (entity.value instanceof Package) {
      return entity.value.cadenceMax;
    }
    // Cadence Plans don't exist - Or do they? *Vsauce music*
    return null;
  }

  const hashedBackground = {
    color: 'white',
    dashArrayX: [1, 0],
    dashArrayY: [2, 5],
    symbolSize: 1,
    rotation: Math.PI / 4,
  };

  function createMarkLine(value: number, label: string, padding: number[]) {
    if (value == 0) {
      return null;
    }
    return {
      xAxis: value,
      label: {
        formatter: `${formatNumber(value, { mantissa: 1, average: true })} ${label}`,
        padding,
      },
      lineStyle: {
        normal: {
          type: 'dashed',
          color: 'grey',
        },
      },
    };
  }

  const barWidth = '50%';
  const barCategoryGap = '1';
  function createBarSerie(data: [number, number], name: string, color: string, itemStyle?) {
    return {
      name,
      type: 'bar',
      stack: 'total',
      color,
      data,
      barWidth,
      barCategoryGap,
      itemStyle,
    };
  }

  const chartOptions: ComputedRef<echarts.EChartsOption> = computed(
    () =>
      ({
        legend: {
          data: [
            forecastMode.value === ForecastMode.Inventory
              ? i18n.global.t('names.inventoryForecast')
              : i18n.global.t('names.audienceForecast'),
            i18n.global.t('names.deliveryGoal'),
          ],
        },
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'shadow',
          },
        },
        xAxis: {
          type: 'value',
        },
        yAxis: {
          type: 'category',
          data: [
            forecastMode.value === ForecastMode.Inventory
              ? i18n.global.t('names.inventoryForecast')
              : i18n.global.t('names.audienceForecast'),
            i18n.global.t('names.deliveryGoal'),
          ],
        },
        series: [
          {
            type: 'bar',
            stack: 'total',
            data: [0, 0],
            barWidth,
            barCategoryGap,
            markLine: {
              tooltip: {
                trigger: 'none',
                axisPointer: {
                  type: 'shadow',
                },
              },
              symbol: ['none', 'none'],
              data: [
                createMarkLine(
                  deliveredImpressions.value,
                  i18n.global.t('availsResult.categories.labels.delivered'),
                  [0, 0, 0, -120],
                ),
                createMarkLine(deliveryGoalTotal.value, i18n.global.t('names.deliveryGoal'), [0, 0, 0, 0]),
                createMarkLine(
                  totalImpressionCapacity.value,
                  i18n.global.t('availsResult.categories.labels.totalCapacity'),
                  [0, -120, -15, 0],
                ),
              ].filter((markLine) => markLine != null),
            },
          },
          createBarSerie(
            [deliveredImpressions.value, deliveredImpressions.value],
            i18n.global.t('availsResult.categories.labels.delivered'),
            DELIVERY_GOAL_COLORS[DeliveryGoals.Delivered],
          ),
          ...(useDetailedForecast.value
            ? [
                createBarSerie(
                  [availableReserved.value, 0],
                  i18n.global.t('availsResult.categories.labels.availableReserved'),
                  getCategoryColor(InventoryForecasts.Available, 'reserved'),
                ),
                createBarSerie(
                  [availableUnreserved.value, 0],
                  i18n.global.t('availsResult.categories.labels.availableUnreserved'),
                  getCategoryColor(InventoryForecasts.Available, 'unreserved'),
                ),
              ]
            : [
                createBarSerie(
                  [available.value, 0],
                  i18n.global.t('availsResult.categories.labels.available'),
                  INVENTORY_FORECAST_COLORS[InventoryForecasts.Available],
                ),
              ]),
          createBarSerie(
            [0, likelyToDeliver.value],
            i18n.global.t('availsResult.categories.labels.likelyToDeliver'),
            DELIVERY_GOAL_COLORS[DeliveryGoals.LikelyToDeliver],
            { decal: hashedBackground },
          ),
          createBarSerie(
            [0, competingGoal.value],
            i18n.global.t('availsResult.categories.labels.competing'),
            DELIVERY_GOAL_COLORS[DeliveryGoals.Competing],
            { decal: hashedBackground },
          ),
          createBarSerie(
            [competing.value, 0],
            i18n.global.t('availsResult.categories.labels.competing'),
            INVENTORY_FORECAST_COLORS[InventoryForecasts.Competing],
          ),
          createBarSerie(
            [booked.value, 0],
            i18n.global.t('availsResult.categories.labels.booked'),
            INVENTORY_FORECAST_COLORS[InventoryForecasts.Booked],
          ),
          createBarSerie(
            [0, unavailable.value],
            i18n.global.t('availsResult.categories.labels.unavailable'),
            DELIVERY_GOAL_COLORS[DeliveryGoals.Unavailable],
          ),
          createBarSerie(
            [remainder.value, 0],
            i18n.global.t('availsResult.categories.labels.capacityRemaining'),
            'lightgrey',
          ),
        ],
        height: '100px',
        grid: {
          left: 0,
          top: '15%',
          right: '5%',
          bottom: 0,
          containLabel: true,
        },
        textStyle: {
          fontFamily: 'Oxygen',
        },
      }) as echarts.EChartsOption,
  );

  function downloadGraph(format: 'EXCEL' | 'CSV' | 'IMAGE') {
    const data = {
      deliveryGoal: {
        delivered: deliveredImpressions.value,
        likelyToDeliver: likelyToDeliver.value,
        competing: competing.value,
        unavailable: unavailable.value,
      },
      forecast: {
        delivered: deliveredImpressions.value,
        available: available.value,
        competing: competing.value,
        booked: booked.value,
      },
    };
    switch (format) {
      case 'EXCEL': {
        return AvailsResultExportHelper.downloadAsXLSX(data);
      }
      case 'CSV': {
        return AvailsResultExportHelper.downloadAsCSV(data);
      }
      case 'IMAGE': {
        return AvailsResultExportHelper.downloadAsImage(echart.value);
      }
      default: {
        throw Error('Unsupported export mode');
      }
    }
  }

  onMounted(async () => {
    const entityAvailsDataHelper = createEntityAvailsDataHelper(entity.value);
    const availsData = await entityAvailsDataHelper.fetchAvailsData();
    estimatedTotalImpressionCapacity.value = Math.round(availsData.estimatedTotalImpressionsCapacity);
    deliveredImpressions.value = Math.round(availsData.deliveredImpressions);
    deliveryGoalTotal.value = Math.round(availsData.deliveryGoal);
    if (isPurchaseTypeCadence.value) {
      targetedCadence.value = getTargetedCadence();
    }
    dataLoaded.value = true;
  });

  defineExpose({
    downloadGraph,
  });
</script>

<style lang="scss" scoped>
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  .avails-result-wrapper {
    display: flex;
    flex-direction: column;
  }

  .title {
    font-weight: 600;
    font-size: 1.6rem;
    padding: 2rem 0;
    margin: 0 1rem;
  }

  .cadence-details {
    display: flex;
    flex-direction: row;
    margin: 0 1rem;
    font-weight: 400;
    font-size: 1.6rem;
    & > * {
      margin-right: 1rem;
    }
  }

  .caption-columns {
    display: grid;
    grid-template-columns: auto auto;
    gap: 2rem;
    border: solid 1px $borders-light;
    padding: 1.6rem;
    background: $almost-white;
    box-shadow: $inner-shadow-top;

    .color-sample {
      top: 0.3rem;
      position: relative;
    }

    .caption-column {
      display: grid;
      grid-template-columns: min-content min-content auto;
      align-items: start;
      gap: 1rem 0.5rem;

      .caption-value {
        margin: 0 1rem;
        display: flex;
        justify-content: end;
        font-family: $decorative-font-family;
        font-weight: 400;
      }

      .caption-label {
        display: flex;
        flex-direction: column;
        align-items: start;
        font-weight: 600;
        i {
          font-weight: 300;
        }
      }

      .caption-value,
      .caption-label {
        font-size: 1.2rem;
      }
    }
  }

  .chart {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 15rem;
    position: relative;
    margin: 0 1rem;

    .echarts {
      height: fit-content;
      min-height: 15rem;
      width: 100%;
    }
  }
</style>
